import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, Trash2, Send, Loader2, CheckCheck } from 'lucide-react';
import { thClass, tdClass } from './adminUi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessages = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // {message, replies}
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const load = () =>
    axios
      .get(`${API}/admin/messages`)
      .then((r) => setItems(r.data.messages || []))
      .catch(() => toast.error('Échec du chargement des messages'))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const open = async (m) => {
    try {
      const { data } = await axios.get(`${API}/admin/messages/${m.id}`);
      setActive(data);
      if (m.status === 'unread') {
        await axios.put(`${API}/admin/messages/${m.id}/read`).catch(() => {});
        load();
      }
    } catch {
      toast.error('Échec du chargement du fil');
    }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/messages/${active.message.id}/reply`, { body: reply });
      toast.success('Réponse envoyée');
      setReply('');
      await open(active.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const remove = async (m) => {
    if (!window.confirm(`Supprimer le message de ${m.name} ?`)) return;
    try {
      await axios.delete(`${API}/admin/messages/${m.id}`);
      toast.success('Message supprimé');
      if (active?.message?.id === m.id) setActive(null);
      load();
    } catch {
      toast.error('Erreur');
    }
  };

  return (
    <div data-testid="admin-messages" className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <h2 className="font-heading text-2xl mb-6">Boîte de réception</h2>
        {loading ? (
          <p className="text-foreground/40">Chargement…</p>
        ) : items.length === 0 ? (
          <p className="text-foreground/40 py-8">Aucun message.</p>
        ) : (
          <div className="border border-black/10 divide-y divide-black/10 max-h-[600px] overflow-y-auto">
            {items.map((m) => (
              <button
                key={m.id}
                onClick={() => open(m)}
                className={`w-full text-left p-4 hover:bg-black/5 transition-colors flex items-start justify-between gap-3 ${active?.message?.id === m.id ? 'bg-black/5' : ''}`}
                data-testid="message-row"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {m.status === 'unread' && <span className="w-2 h-2 rounded-full bg-gold shrink-0" />}
                    <span className={`truncate ${m.status === 'unread' ? 'text-foreground font-medium' : 'text-foreground/70'}`}>{m.name}</span>
                    {m.status === 'replied' && <CheckCheck size={14} className="text-gold shrink-0" />}
                  </div>
                  <p className="text-xs text-foreground/40 truncate">{m.subject}</p>
                </div>
                <Trash2 size={15} className="text-foreground/30 hover:text-red-400 shrink-0" onClick={(e) => { e.stopPropagation(); remove(m); }} />
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        {!active ? (
          <div className="text-foreground/40 py-12 flex items-center gap-2"><Mail size={18} /> Sélectionne un message.</div>
        ) : (
          <div className="border border-black/10 p-5">
            <h3 className="font-heading text-xl">{active.message.subject}</h3>
            <p className="text-sm text-foreground/50 mb-1">{active.message.name} · {active.message.email}</p>
            <p className="text-xs text-foreground/30 mb-4">{(active.message.created_at || '').slice(0, 16).replace('T', ' ')}</p>
            <p className="text-foreground/80 whitespace-pre-wrap border-l-2 border-black/10 pl-4 mb-6">{active.message.message}</p>

            {active.replies.length > 0 && (
              <div className="space-y-3 mb-6">
                {active.replies.map((r, i) => (
                  <div key={i} className="bg-gold/5 border border-gold/15 p-3">
                    <p className="text-xs text-gold/70 mb-1">Votre réponse · {(r.created_at || '').slice(0, 16).replace('T', ' ')}</p>
                    <p className="text-foreground/80 whitespace-pre-wrap text-sm">{r.body}</p>
                  </div>
                ))}
              </div>
            )}

            <textarea
              className="w-full bg-transparent border border-black/15 focus:border-gold focus:outline-none px-4 py-3 text-foreground min-h-[120px]"
              placeholder={`Répondre à ${active.message.name}…`}
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              data-testid="message-reply-body"
            />
            <button onClick={sendReply} disabled={sending} className="btn-primary inline-flex items-center gap-2 mt-3" data-testid="message-reply-send">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Envoyer la réponse
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMessages;
